import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { PDFDocument } from 'pdf-lib';
import { Upload, FileText, Users } from 'lucide-react';
import { toast } from 'react-toastify';
import { useChainContext } from '@/containers/context/context';
import {
  getApprover,
  sendSafeBoxOtp,
  addSafeboxApp,
} from '@/utils/services/user.service';
import {
  ModalOverlay,
  ModalBox,
  ModalTitle,
  ModalActions,
  ColorInput,
  ColorLabel,
  ColorRow,
  ColorValue,
  Canvas,
  BtnGhost,
  BtnPrimary,
  OTPInput,
  OTPProgressBar,
  OTPProgressFill,
  PageWrapper,
  InnerWrapper,
  StickyHeader,
  HeaderTitle,
  HeaderSub,
  HeaderActions,
  BtnSuccess,
  BtnDanger,
  GridLayout,
  MetadataCard,
  SignersCard,
  DocumentsCard,
  CardHeaderRow,
  CardTitle,
  Input,
  Textarea,
  TextareaSmall,
  HashBox,
  HashLabel,
  HashRow,
  HashInputReadonly,
  HashActions,
  LinkBtn,
  SignerHeaderRow,
  ChatBtn,
  ChatBtnWrap,
  ChatTooltip,
  SignerInputRow,
  SignerSearchWrap,
  SignerItem,
  SignerRemoveBtn,
  ApproveSignersBtn,
  StatusBox,
  StatusLabel,
  StatusProgressHeader,
  ProgressBar,
  ProgressFill,
  StatusEmpty,
  StatusItem,
  MutedText,
  DropZone,
  DocItem,
  DocItemInner,
  DocName,
  DocSignBtn,
  SigPreviewWrap,
  SigPreviewLabel,
  SigPreviewImg,
  DropZoneLabel,
  PdfPreviewLayout,
  PdfPreviewMain,
  PdfPreviewSidebar,
  PageNav,
  ModalBoxNarrow,
  SuccessModalBox,
  SuccessIconWrap,
  BtnSmall,
} from './CreateContract.styled';

// ------------------------------
// Signature Creator Modal
// ------------------------------
function SignatureCreatorModal({ onSave, onClose }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [strokeColor, setStrokeColor] = useState('#00f7d2');

  // Fill canvas with white background on mount
  useEffect(() => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  }, []);

  const start = () => (drawing.current = true);
  const end = () => {
    drawing.current = false;
    canvasRef.current?.getContext('2d')?.beginPath();
  };

  const draw = (e) => {
    if (!drawing.current || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    const r = canvasRef.current.getBoundingClientRect();
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineTo(e.clientX - r.left, e.clientY - r.top);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(e.clientX - r.left, e.clientY - r.top);
  };

  const save = () => {
    if (!canvasRef.current) return;
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    ctx.save();
    ctx.font = '10px Inter, system-ui';
    ctx.fillStyle = 'rgba(0,247,210,.6)';
    ctx.fillText(
      `Custodiy Electronic Signature · ${new Date().toISOString()}`,
      10,
      canvasRef.current.height - 10
    );
    ctx.restore();
    onSave(canvasRef.current.toDataURL('image/png'));
  };

  return (
    <ModalOverlay>
      <ModalBox width="520px">
        <ModalTitle style={{ marginBottom: 12 }}>Create signature</ModalTitle>
        <ColorRow>
          <ColorLabel>Signature color</ColorLabel>
          <ColorInput
            type="color"
            value={strokeColor}
            onChange={(e) => setStrokeColor(e.target.value)}
          />
          <ColorValue>{strokeColor}</ColorValue>
        </ColorRow>
        <Canvas
          ref={canvasRef}
          width={480}
          height={180}
          onMouseDown={start}
          onMouseUp={end}
          onMouseMove={draw}
        />
        <ModalActions>
          <BtnGhost type="button" onClick={onClose}>
            Cancel
          </BtnGhost>
          <BtnPrimary type="button" onClick={save}>
            Save signature
          </BtnPrimary>
        </ModalActions>
      </ModalBox>
    </ModalOverlay>
  );
}

// ------------------------------
// PDF Preview Modal
// ------------------------------
function PdfPreviewModal({ file, signature, onConfirm, onClose }) {
  const [page, setPage] = useState(1);

  return (
    <ModalOverlay>
      <PdfPreviewLayout>
        <PdfPreviewMain>
          <iframe
            title="pdf"
            src={URL.createObjectURL(file) + `#page=${page}`}
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        </PdfPreviewMain>
        <PdfPreviewSidebar>
          <ModalTitle>Sign document</ModalTitle>
          <p style={{ fontSize: 12, opacity: 0.7, margin: 0 }}>
            Signatures will be placed at the bottom-right of each page.
          </p>
          <PageNav>
            <BtnSmall
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </BtnSmall>
            <span>Page {page}</span>
            <BtnSmall type="button" onClick={() => setPage((p) => p + 1)}>
              Next
            </BtnSmall>
          </PageNav>
          <BtnPrimary
            type="button"
            onClick={() => onConfirm()}
            style={{ marginTop: 'auto' }}
          >
            Sign document
          </BtnPrimary>
          <BtnGhost type="button" onClick={onClose}>
            Cancel
          </BtnGhost>
        </PdfPreviewSidebar>
      </PdfPreviewLayout>
    </ModalOverlay>
  );
}

// ------------------------------
// OTP Confirm Modal
// ------------------------------
function OtpConfirmModal({ action, otp, setOtp, remaining, duration, onResend, onConfirm, onClose }) {
  const progress = (remaining / duration) * 100;

  return (
    <ModalOverlay>
      <ModalBoxNarrow>
        <ModalTitle style={{ marginBottom: 8 }}>
          {action === 'approve' ? 'Confirm approval' : 'Confirm rejection'}
        </ModalTitle>
        <p style={{ fontSize: 14, opacity: 0.7, marginBottom: 16 }}>
          Enter the one-time password sent to you
        </p>
        <OTPInput
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
          maxLength={6}
          placeholder="••••••"
        />
        <OTPProgressBar>
          <OTPProgressFill style={{ width: `${progress}%` }} />
        </OTPProgressBar>
        <p style={{ fontSize: 12, opacity: 0.6, marginBottom: 16 }}>
          {remaining > 0 ? `Resend available in ${remaining}s` : 'You can resend the OTP'}
        </p>
        <ModalActions style={{ justifyContent: 'space-between' }}>
          <BtnGhost type="button" onClick={onClose}>
            Cancel
          </BtnGhost>
          <div style={{ display: 'flex', gap: 8 }}>
            <BtnGhost
              type="button"
              disabled={remaining > 0}
              onClick={onResend}
            >
              Resend
            </BtnGhost>
            <BtnPrimary
              type="button"
              onClick={onConfirm}
              disabled={otp.length < 6}
            >
              Confirm
            </BtnPrimary>
          </div>
        </ModalActions>
      </ModalBoxNarrow>
    </ModalOverlay>
  );
}

// ------------------------------
// Safebox Manager Canvas
// ------------------------------
export default function CreateContract() {
  const history = useHistory();
  const { socket } = useChainContext();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [documents, setDocuments] = useState([]);
  const [signedDocs, setSignedDocs] = useState(new Set());
  const [signers, setSigners] = useState([]);
  const [signerDetails, setSignerDetails] = useState([]);
  const [signerSearchInput, setSignerSearchInput] = useState('');
  const [signerSearchResults, setSignerSearchResults] = useState([]);
  const [signerSearchLoading, setSignerSearchLoading] = useState(false);
  const [signerDropdownOpen, setSignerDropdownOpen] = useState(false);
  const signerSearchTimeoutRef = useRef(null);
  const [pendingSigners, setPendingSigners] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [activeDocumentIndex, setActiveDocumentIndex] = useState(null);
  const [signaturePreviews, setSignaturePreviews] = useState({});
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(null);
  const [otp, setOtp] = useState('');
  const OTP_DURATION = 180;
  const [otpRemaining, setOtpRemaining] = useState(OTP_DURATION);
  const [showAddSafeboxModal, setShowAddSafeboxModal] = useState(false);
  const [addSafeboxOtp, setAddSafeboxOtp] = useState('');
  const [addSafeboxLoading, setAddSafeboxLoading] = useState(false);
  const [addSafeboxOtpLoading, setAddSafeboxOtpLoading] = useState(false);
  const ADD_SAFEBOX_OTP_COOLDOWN = 60;
  const [addSafeboxOtpRemaining, setAddSafeboxOtpRemaining] = useState(ADD_SAFEBOX_OTP_COOLDOWN);

  useEffect(() => {
    if (!showOtpModal) return;
    const t = setInterval(() => {
      setOtpRemaining((r) => Math.max(0, r - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [showOtpModal]);

  useEffect(() => {
    if (!showAddSafeboxModal) return;
    setAddSafeboxOtpRemaining(ADD_SAFEBOX_OTP_COOLDOWN);
    const t = setInterval(() => {
      setAddSafeboxOtpRemaining((r) => Math.max(0, r - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [showAddSafeboxModal]);

  const handleSignerSearch = useCallback((value) => {
    setSignerSearchInput(value);
    setSignerDropdownOpen(false);
    if (signerSearchTimeoutRef.current) clearTimeout(signerSearchTimeoutRef.current);
    if (!value || value.length < 2) {
      setSignerSearchResults([]);
      return;
    }
    signerSearchTimeoutRef.current = setTimeout(async () => {
      setSignerSearchLoading(true);
      try {
        const res = await getApprover({ name: value });
        const users = res?.data?.user || [];
        const search = value.toLowerCase().trim();
        // Only show dropdown for exact full name matches
        const exactMatches = users.filter((u) => {
          const name = (u.name || '').toLowerCase().trim();
          return name === search;
        });
        setSignerSearchResults(exactMatches);
        setSignerDropdownOpen(exactMatches.length > 0);
      } catch {
        setSignerSearchResults([]);
        setSignerDropdownOpen(false);
      } finally {
        setSignerSearchLoading(false);
      }
    }, 300);
  }, []);

  const handleSignerSelect = useCallback((user) => {
    if (!user) return;
    const address = user.secondary_wallet_address || user.address || user.wallet_address;
    const email = user.email || user.address;
    if (!address && !email) return;
    const addr = address || email;
    if (pendingSigners.find((p) => p.address === addr || p.email === email)) {
      toast.info('Signer already added');
      setSignerSearchInput('');
      setSignerSearchResults([]);
      setSignerDropdownOpen(false);
      return;
    }
    setPendingSigners((prev) =>
      prev.concat({
        _id: user._id || user.user_id,
        name: user.name || user.email || addr,
        email,
        address: addr,
        tag: email?.includes('@') ? 'email' : 'address',
      })
    );
    setSignerSearchInput('');
    setSignerSearchResults([]);
    setSignerDropdownOpen(false);
  }, [pendingSigners]);

  const handleUpload = (e) => {
    if (!e.target.files) return;
    setDocuments((prev) => prev.concat(Array.from(e.target.files)));
  };

  const handleChatWithSigners = useCallback(() => {
    const targetSigner = signerDetails.find((s) => s.user_id);
    if (!targetSigner?.user_id) {
      toast.error('Signer user information is not available for chat');
      return;
    }
    if (!socket) {
      toast.error('Chat service is not available. Please try again later.');
      return;
    }
    try {
      const currentUserId = JSON.parse(localStorage.getItem('user') || '{}')?.data?.user?.user_id;
      if (!currentUserId) {
        toast.error('Please login to chat with signers');
        return;
      }
      const signerName = targetSigner.name || targetSigner.email || 'Signer';
      socket.emit(
        'startChat',
        {
          subject: 'Start New One to One Chat',
          user_id: currentUserId,
          user_id_2: targetSigner.user_id,
          type: 'user',
        },
        () => {}
      );
      history.push(`/livechat?userId=${targetSigner.user_id}&name=${encodeURIComponent(signerName)}`);
    } catch (error) {
      console.error('Error starting chat:', error);
      toast.error('Failed to start chat. Please try again.');
    }
  }, [signerDetails, socket, history]);


  const handleResendAddSafeboxOtp = () => {
    setAddSafeboxOtpRemaining(ADD_SAFEBOX_OTP_COOLDOWN);
    sendSafeBoxOtp()
      .then((response) => {
        toast.success(response?.data?.message || 'OTP resent to your email');
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message || 'Failed to resend OTP');
      });
  };

  const handleAddSafeboxClick = async () => {
    if (!title?.trim()) {
      toast.error('Safebox title is required');
      return;
    }
    if (!description?.trim()) {
      toast.error('Safebox description is required');
      return;
    }
    if (!internalNotes?.trim()) {
      toast.error('Internal notes are required');
      return;
    }
    const approversList = [
      ...signers,
      ...pendingSigners.map((p) => p.address || p.email).filter(Boolean),
    ];
    if (approversList.length === 0) {
      toast.error('Please add at least one signer');
      return;
    }
    if (documents.length === 0) {
      toast.error('Please add at least one document');
      return;
    }
    setAddSafeboxLoading(true);
    sendSafeBoxOtp()
      .then((response) => {
        setAddSafeboxLoading(false);
        toast.success(response?.data?.message || 'OTP sent to your email');
        setShowAddSafeboxModal(true);
        setAddSafeboxOtp('');
      })
      .catch((err) => {
        setAddSafeboxLoading(false);
        toast.error(err?.response?.data?.message || err?.message || 'Failed to send OTP');
      });
  };

  const handleConfirmAddSafebox = async () => {
    if (!addSafeboxOtp || addSafeboxOtp.length < 4) {
      toast.error('Please enter a valid OTP');
      return;
    }
    const approversList = [
      ...signers,
      ...pendingSigners.map((p) => p.address || p.email).filter(Boolean),
    ];
    setAddSafeboxOtpLoading(true);
    try {
      const fd = new FormData();
      fd.append('approvers', JSON.stringify(approversList));
      fd.append('name', title.trim());
      fd.append('note', description.trim());
      fd.append('internal_note', internalNotes.trim());
      fd.append('File', documents[0]);
      fd.append('otp', addSafeboxOtp);
      await addSafeboxApp(fd);
      toast.success('Safebox added successfully');
      setShowAddSafeboxModal(false);
      setAddSafeboxOtp('');
    } catch (err) {
      toast.error(err?.response?.data?.Message || err?.message || 'Failed to add safebox');
    } finally {
      setAddSafeboxOtpLoading(false);
    }
  };

  return (
    <PageWrapper>
      <InnerWrapper>
        <StickyHeader>
          <div>
            <HeaderTitle>Safebox Manager</HeaderTitle>
            <HeaderSub>Draft · Sign · Decision</HeaderSub>
          </div>
          <HeaderActions>
            <BtnSuccess
              type="button"
              disabled={
                !title?.trim() ||
                !description?.trim() ||
                !internalNotes?.trim() ||
                (signers.length === 0 && pendingSigners.length === 0) ||
                documents.length === 0 ||
                addSafeboxLoading
              }
              onClick={handleAddSafeboxClick}
            >
              {addSafeboxLoading ? 'Sending OTP…' : 'Save & Approve'}
            </BtnSuccess>
            <BtnDanger type="button" onClick={() => history.push('/contractsLists')}>
              Reject
            </BtnDanger>
          </HeaderActions>
        </StickyHeader>

        <GridLayout>
          <MetadataCard>
            <CardHeaderRow>
              <FileText size={16} />
              <CardTitle>Safebox Metadata</CardTitle>
            </CardHeaderRow>
            <Input
              placeholder="Safebox title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <Textarea
              placeholder="Safebox description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <TextareaSmall
              placeholder="Internal notes (internal use only)"
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
            />
            {/* <HashBox>
              <HashLabel>Safebox hash</HashLabel>
              <HashRow>
                <HashInputReadonly readOnly value="0x3f9a…c82e (example)" />
                <HashActions>
                  <LinkBtn href="#" target="_blank" rel="noreferrer">
                    View
                  </LinkBtn>
                  <BtnPrimary
                    type="button"
                    onClick={() => navigator.clipboard.writeText('0x3f9a…c82e')}
                  >
                    Copy
                  </BtnPrimary>
                </HashActions>
              </HashRow>
            </HashBox> */}
          </MetadataCard>

          <SignersCard>
            <SignerHeaderRow>
              <CardHeaderRow style={{ marginBottom: 0 }}>
                <Users size={16} />
                <CardTitle>Signers</CardTitle>
              </CardHeaderRow>
              <ChatBtnWrap>
                <ChatBtn
                  type="button"
                  disabled={signers.length === 0}
                  onClick={handleChatWithSigners}
                >
                  Chat with signers
                </ChatBtn>
                {signers.length === 0 && (
                  <ChatTooltip>Add at least one signer to start a chat</ChatTooltip>
                )}
              </ChatBtnWrap>
            </SignerHeaderRow>
            <SignerInputRow>
              <SignerSearchWrap style={{ position: 'relative' }}>
                <Input
                  type="text"
                  value={signerSearchInput}
                  onChange={(e) => handleSignerSearch(e.target.value)}
                  onBlur={() => setTimeout(() => setSignerDropdownOpen(false), 150)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && signerSearchResults.length > 0) {
                      e.preventDefault();
                      handleSignerSelect(signerSearchResults[0]);
                    } else if (e.key === 'Escape') {
                      setSignerDropdownOpen(false);
                    }
                  }}
                  placeholder="Search signer by name..."
                  style={{ background: 'rgba(0, 0, 0, 0.4)', borderColor: 'rgba(255, 255, 255, 0.1)', color: '#fff' }}
                />
                {signerDropdownOpen && signerSearchResults.length > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: 'calc(100% + 4px)',
                      zIndex: 10,
                      background: '#0f172a',
                      border: '1px solid rgba(6, 182, 212, 0.2)',
                      borderRadius: 12,
                      maxHeight: 200,
                      overflowY: 'auto',
                    }}
                  >
                    {signerSearchResults.map((user) => {
                      const label = user.name || user.email || user._id;
                      return (
                        <div
                          key={user._id}
                          onMouseDown={(e) => { e.preventDefault(); handleSignerSelect(user); }}
                          style={{
                            padding: '10px 14px',
                            cursor: 'pointer',
                            color: '#fff',
                            background: 'transparent',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(6, 182, 212, 0.1)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          {label}
                        </div>
                      );
                    })}
                  </div>
                )}
              </SignerSearchWrap>
            </SignerInputRow>
            {pendingSigners.map((s) => (
              <SignerItem key={s.address || s.email}>
                <span>{s.name || s.email || s.address}</span>
                <SignerRemoveBtn
                  type="button"
                  onClick={() =>
                    setPendingSigners((prev) =>
                      prev.filter((x) => (x.address || x.email) !== (s.address || s.email))
                    )
                  }
                >
                  remove
                </SignerRemoveBtn>
              </SignerItem>
            ))}
            <ApproveSignersBtn
              type="button"
              disabled={pendingSigners.length === 0}
              onClick={() => {
                const approved = pendingSigners.map((p) => p.address || p.email);
                setSigners((prev) => prev.concat(approved));
                setSignerDetails((prev) =>
                  prev.concat(
                    pendingSigners.map((p) => ({
                      address: p.address,
                      email: p.email,
                      user_id: p._id,
                      name: p.name,
                    }))
                  )
                );
                setNotifications((prev) =>
                  prev.concat(
                    approved.map((email) => ({
                      email,
                      opened: false,
                      approved: null,
                      notifiedAt: new Date().toISOString(),
                    }))
                  )
                );
                setPendingSigners([]);
              }}
            >
              Approve signer list
            </ApproveSignersBtn>
            <StatusBox>
              <StatusLabel>Signer status</StatusLabel>
              <StatusProgressHeader>
                <span>Progress</span>
                <span>
                  {notifications.filter((n) => n.approved === true).length}/{notifications.length}
                </span>
              </StatusProgressHeader>
              <ProgressBar>
                <ProgressFill
                  style={{
                    width:
                      notifications.length === 0
                        ? '0%'
                        : `${(notifications.filter((n) => n.approved === true).length / notifications.length) * 100}%`,
                  }}
                />
              </ProgressBar>
              {notifications.length === 0 && (
                <StatusEmpty>No signers notified yet</StatusEmpty>
              )}
              {notifications.map((n) => (
                <StatusItem key={n.email}>
                  <div>
                    <div>{n.email}</div>
                    <MutedText>sent {new Date(n.notifiedAt).toLocaleString()}</MutedText>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ color: n.opened ? '#67e8f9' : 'rgba(255,255,255,0.4)' }}>
                      {n.opened ? 'Opened' : 'Not opened'}
                    </span>
                    {n.approved === null && (
                      <span style={{ color: '#fde047' }}>Pending</span>
                    )}
                    {n.approved === true && (
                      <span style={{ color: '#86efac' }}>Approved</span>
                    )}
                    {n.approved === false && (
                      <span style={{ color: '#fca5a5' }}>Rejected</span>
                    )}
                  </div>
                </StatusItem>
              ))}
            </StatusBox>
          </SignersCard>

          <DocumentsCard>
            <CardHeaderRow>
              <Upload size={16} />
              <CardTitle>Documents</CardTitle>
            </CardHeaderRow>
            <DropZone
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const files = Array.from(e.dataTransfer.files).filter(
                  (f) => f.type === 'application/pdf'
                );
                if (files.length) setDocuments((prev) => prev.concat(files));
              }}
            >
              <Upload size={28} style={{ opacity: 0.7 }} />
              <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>
                Drag &amp; drop PDF documents here
              </p>
              <DropZoneLabel>
                Browse files
                <input
                  type="file"
                  multiple
                  accept="application/pdf"
                  onChange={handleUpload}
                  hidden
                />
              </DropZoneLabel>
            </DropZone>
            {documents.map((doc, i) => (
              <DocItem key={i} signed={signedDocs.has(i)}>
                <DocItemInner>
                  <DocName>{doc.name}</DocName>
                  <DocSignBtn
                    type="button"
                    onClick={() => {
                      setActiveDocumentIndex(i);
                      setShowSignatureModal(true);
                    }}
                  >
                    Sign
                  </DocSignBtn>
                </DocItemInner>
                {signedDocs.has(i) && signaturePreviews[i] && (
                  <SigPreviewWrap>
                    <SigPreviewLabel>Signature preview</SigPreviewLabel>
                    <SigPreviewImg src={signaturePreviews[i]} alt="Signature preview" />
                  </SigPreviewWrap>
                )}
              </DocItem>
            ))}
          </DocumentsCard>

        </GridLayout>
      </InnerWrapper>

      {showSignatureModal && (
        <SignatureCreatorModal
          onSave={(img) => {
            if (activeDocumentIndex !== null) {
              setSignedDocs((prev) => new Set(prev).add(activeDocumentIndex));
              setSignaturePreviews((prev) => ({ ...prev, [activeDocumentIndex]: img }));
            }
            setShowSignatureModal(false);
            setShowPdfPreview(true);
          }}
          onClose={() => setShowSignatureModal(false)}
        />
      )}

      {showPdfPreview &&
        activeDocumentIndex !== null &&
        signaturePreviews[activeDocumentIndex] && (
          <PdfPreviewModal
            file={documents[activeDocumentIndex]}
            signature={signaturePreviews[activeDocumentIndex]}
            onConfirm={async () => {
              const pdfBytes = await documents[activeDocumentIndex].arrayBuffer();
              const pdfDoc = await PDFDocument.load(pdfBytes);
              const pngBytes = await fetch(signaturePreviews[activeDocumentIndex]).then((r) =>
                r.arrayBuffer()
              );
              const pngImage = await pdfDoc.embedPng(pngBytes);

              const scale = 0.25;
              const margin = 40;
              const imgWidth = pngImage.width * scale;
              const imgHeight = pngImage.height * scale;
              const pageCount = pdfDoc.getPageCount();

              for (let i = 0; i < pageCount; i++) {
                const pg = pdfDoc.getPage(i);
                const { width } = pg.getSize();
                pg.drawImage(pngImage, {
                  x: width - imgWidth - margin,
                  y: margin,
                  width: imgWidth,
                  height: imgHeight,
                });
              }

              const signedPdfBytes = await pdfDoc.save();
              const blob = new Blob([signedPdfBytes], { type: 'application/pdf' });
              const url = URL.createObjectURL(blob);

              const a = document.createElement('a');
              a.href = url;
              a.download = documents[activeDocumentIndex].name.replace('.pdf', '_signed.pdf');
              a.click();

              URL.revokeObjectURL(url);
              setShowPdfPreview(false);
            }}
            onClose={() => setShowPdfPreview(false)}
          />
        )}

      {showOtpModal && (
        <OtpConfirmModal
          action={showOtpModal}
          otp={otp}
          setOtp={setOtp}
          remaining={otpRemaining}
          duration={OTP_DURATION}
          onResend={() => setOtpRemaining(OTP_DURATION)}
          onConfirm={() => {
            setShowOtpModal(null);
            if (showOtpModal === 'approve') setShowApproveModal(true);
          }}
          onClose={() => setShowOtpModal(null)}
        />
      )}

      {showApproveModal && (
        <ModalOverlay>
          <SuccessModalBox>
            <SuccessIconWrap>✓</SuccessIconWrap>
            <ModalTitle style={{ marginBottom: 8 }}>Safebox approved</ModalTitle>
            <p style={{ fontSize: 14, opacity: 0.7, marginBottom: 16 }}>
              The safebox has been successfully saved and approved.
            </p>
            <BtnPrimary type="button" onClick={() => setShowApproveModal(false)}>
              Close
            </BtnPrimary>
          </SuccessModalBox>
        </ModalOverlay>
      )}

      {showAddSafeboxModal && (
        <ModalOverlay>
          <ModalBoxNarrow>
            <ModalTitle style={{ marginBottom: 8 }}>Enter OTP</ModalTitle>
            <p style={{ fontSize: 14, opacity: 0.7, marginBottom: 16 }}>
              Enter the one-time password sent to your email
            </p>
            <OTPInput
              value={addSafeboxOtp}
              onChange={(e) =>
                setAddSafeboxOtp(e.target.value.replace(/\D/g, '').slice(0, 8))
              }
              maxLength={8}
              placeholder="••••••"
            />
            <OTPProgressBar>
              <OTPProgressFill
                style={{
                  width: `${(addSafeboxOtpRemaining / ADD_SAFEBOX_OTP_COOLDOWN) * 100}%`,
                }}
              />
            </OTPProgressBar>
            <p style={{ fontSize: 12, opacity: 0.6, marginBottom: 16 }}>
              {addSafeboxOtpRemaining > 0
                ? `Resend available in ${addSafeboxOtpRemaining}s`
                : 'You can resend the OTP'}
            </p>
            <ModalActions style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <BtnGhost
                  type="button"
                  onClick={() => {
                    setShowAddSafeboxModal(false);
                    setAddSafeboxOtp('');
                  }}
                >
                  Cancel
                </BtnGhost>
                <BtnGhost
                  type="button"
                  disabled={addSafeboxOtpRemaining > 0}
                  onClick={handleResendAddSafeboxOtp}
                >
                  Resend OTP
                </BtnGhost>
              </div>
              <BtnPrimary
                type="button"
                disabled={addSafeboxOtp.length < 4 || addSafeboxOtpLoading}
                onClick={handleConfirmAddSafebox}
              >
                {addSafeboxOtpLoading ? 'Adding...' : 'Add Safebox'}
              </BtnPrimary>
            </ModalActions>
          </ModalBoxNarrow>
        </ModalOverlay>
      )}
    </PageWrapper>
  );
}
